import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { galleriesApi } from '@/lib/api';
import { Plus, Edit, Trash2, Image, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const AdminGalleries = () => {
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '' });

  useEffect(() => { loadGalleries(); }, []);

  const loadGalleries = async () => {
    try {
      const data = await galleriesApi.getAll();
      setGalleries(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGalleryPhotos = async (galleryId) => {
    try {
      const data = await galleriesApi.getOne(galleryId);
      setSelectedGallery(data);
      setPhotos(data.photos || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '' });
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ title: item.title, description: item.description || '' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error('Please enter a title');
      return;
    }

    try {
      if (editingItem) {
        await galleriesApi.update(editingItem.id, formData);
        toast.success('Gallery updated');
      } else {
        await galleriesApi.create(formData);
        toast.success('Gallery created');
      }
      setDialogOpen(false);
      resetForm();
      loadGalleries();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this gallery and all its photos?')) return;
    try {
      await galleriesApi.delete(id);
      toast.success('Deleted');
      loadGalleries();
      if (selectedGallery?.id === id) {
        setSelectedGallery(null);
        setPhotos([]);
      }
    } catch (error) {
      toast.error('Failed');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await galleriesApi.deletePhoto(photoId);
      toast.success('Photo deleted');
      if (selectedGallery) loadGalleryPhotos(selectedGallery.id);
    } catch (error) {
      toast.error('Failed');
    }
  };

  const handleApprovePhoto = async (photoId) => {
    try {
      await galleriesApi.approvePhoto(photoId);
      toast.success('Photo approved');
      if (selectedGallery) loadGalleryPhotos(selectedGallery.id);
    } catch (error) {
      toast.error('Failed');
    }
  };

  const pendingPhotos = photos.filter(p => !p.is_approved);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Photo Galleries</h1>
          <p className="text-stone-600">Manage photo galleries and uploads</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-amber-700 hover:bg-amber-800"><Plus className="h-4 w-4 mr-2" /> Create Gallery</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Gallery' : 'Create Gallery'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                <Button type="submit" className="bg-amber-700 hover:bg-amber-800">{editingItem ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Gallery List */}
        <div className="lg:col-span-1">
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-stone-800 mb-4">Galleries</h3>
              {loading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-700 mx-auto"></div>
              ) : galleries.length > 0 ? (
                <div className="space-y-2">
                  {galleries.map((gallery) => (
                    <div
                      key={gallery.id}
                      className={`p-3 rounded-lg cursor-pointer transition-colors flex items-center justify-between ${
                        selectedGallery?.id === gallery.id ? 'bg-amber-100' : 'hover:bg-stone-100'
                      }`}
                      onClick={() => loadGalleryPhotos(gallery.id)}
                    >
                      <div>
                        <div className="font-medium text-stone-800">{gallery.title}</div>
                        <div className="text-sm text-stone-500">{gallery.description}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(gallery); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleDelete(gallery.id); }}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-stone-500 text-center py-4">No galleries yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Photos Grid */}
        <div className="lg:col-span-2">
          {selectedGallery ? (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-stone-800">{selectedGallery.title} Photos</h3>
                  {pendingPhotos.length > 0 && (
                    <span className="text-sm text-amber-700">{pendingPhotos.length} pending approval</span>
                  )}
                </div>
                {photos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {photos.map((photo) => (
                      <div key={photo.id} className="relative group">
                        <div className="aspect-square bg-stone-200 rounded-lg overflow-hidden">
                          <img
                            src={photo.photo_data || photo.photo_url}
                            alt={photo.title || 'Photo'}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {!photo.is_approved && (
                          <div className="absolute top-2 left-2 bg-amber-500 text-white text-xs px-2 py-1 rounded">Pending</div>
                        )}
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!photo.is_approved && (
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8 w-8 p-0" onClick={() => handleApprovePhoto(photo.id)}>
                              <Check className="h-4 w-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="destructive" className="h-8 w-8 p-0" onClick={() => handleDeletePhoto(photo.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        {photo.uploaded_by_name && (
                          <div className="text-xs text-stone-500 mt-1">by {photo.uploaded_by_name}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Image className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                    <p className="text-stone-600">No photos in this gallery yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Image className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                <p className="text-stone-600">Select a gallery to view photos</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminGalleries;
